import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Star,
  ArrowLeft,
  Calendar as CalendarIcon,
  Clock,
  Plus,
  XCircle,
  CheckCircle,
  User
} from "lucide-react";
import PeerSupportDisclaimer from "../components/PeerSupportDisclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appointments = ({ user }) => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [responders, setResponders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [selectedResponder, setSelectedResponder] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancelAppointmentId, setCancelAppointmentId] = useState(null);

  const isVeteran = user?.role === "veteran";

  useEffect(() => {
    loadAppointments();
    if (isVeteran) {
      loadResponders();
    }
  }, []);

  const loadAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error("Error loading appointments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadResponders = async () => {
    try {
      const response = await axios.get(`${API}/users/responders`);
      setResponders(response.data);
    } catch (error) {
      console.error("Error loading responders:", error);
    }
  };

  const handleCreateAppointment = async () => {
    if (!selectedDate || !selectedResponder) {
      toast.error("Please select a date and responder");
      return;
    }

    setIsSubmitting(true);
    try {
      const [hours, minutes] = selectedTime.split(":");
      const scheduledAt = new Date(selectedDate);
      scheduledAt.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      await axios.post(`${API}/appointments`, {
        responder_id: selectedResponder,
        scheduled_at: scheduledAt.toISOString(),
        notes: notes || null
      });

      toast.success("Appointment scheduled!");
      setShowNewDialog(false);
      setSelectedDate(null);
      setSelectedTime("10:00");
      setSelectedResponder("");
      setNotes("");
      loadAppointments();
    } catch (error) {
      toast.error("Failed to schedule appointment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!cancelAppointmentId) return;

    try {
      await axios.post(`${API}/appointments/${cancelAppointmentId}/cancel`);
      toast.success("Appointment cancelled");
      loadAppointments();
    } catch (error) {
      toast.error("Failed to cancel appointment");
    } finally {
      setCancelAppointmentId(null);
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric"
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const upcomingAppointments = appointments.filter(
    apt => apt.status === "scheduled" && new Date(apt.scheduled_at) > new Date()
  );
  const pastAppointments = appointments.filter(
    apt => apt.status !== "scheduled" || new Date(apt.scheduled_at) <= new Date()
  );

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
    "15:00", "15:30", "16:00", "16:30", "17:00"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Star className="w-6 h-6 text-primary" />
              <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Appointments
              </span>
              <span className="text-xs text-primary font-bold">™</span>
            </div>
          </div>
          {isVeteran && (
            <Button 
              onClick={() => setShowNewDialog(true)}
              size="sm"
              data-testid="new-appointment-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
          )}
        </div>
      </header>

      <main className="pt-16 pb-8 px-4 max-w-3xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-8 py-6">
            {/* Upcoming Appointments */}
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
                <CalendarIcon className="w-5 h-5 inline mr-2 text-secondary" />
                Upcoming Appointments
              </h2>
              
              {upcomingAppointments.length === 0 ? (
                <Card className="card-tactical">
                  <CardContent className="py-8 text-center">
                    <CalendarIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No upcoming appointments</p>
                    {isVeteran && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setShowNewDialog(true)}
                      >
                        Schedule One
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {upcomingAppointments.map((apt) => (
                    <Card key={apt.appointment_id} className="card-tactical" data-testid={`appointment-${apt.appointment_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-12 h-12">
                            <AvatarFallback className="bg-secondary text-secondary-foreground">
                              {getInitials(isVeteran ? apt.responder_name : apt.veteran_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                              {isVeteran ? apt.responder_name : apt.veteran_name}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4" />
                                {formatDate(apt.scheduled_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatTime(apt.scheduled_at)}
                              </span>
                            </div>
                            {apt.notes && (
                              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                {apt.notes}
                              </p>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => setCancelAppointmentId(apt.appointment_id)}
                            data-testid={`cancel-apt-${apt.appointment_id}`}
                          >
                            <XCircle className="w-5 h-5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>

            {/* Past Appointments */}
            {pastAppointments.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
                  <Clock className="w-5 h-5 inline mr-2 text-muted-foreground" />
                  Past Appointments
                </h2>
                
                <div className="space-y-3">
                  {pastAppointments.map((apt) => (
                    <Card key={apt.appointment_id} className="card-tactical opacity-75">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-muted text-muted-foreground">
                              {getInitials(isVeteran ? apt.responder_name : apt.veteran_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium text-foreground">
                              {isVeteran ? apt.responder_name : apt.veteran_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(apt.scheduled_at)} at {formatTime(apt.scheduled_at)}
                            </p>
                          </div>
                          <Badge 
                            variant={apt.status === "completed" ? "secondary" : "destructive"}
                            className="text-xs"
                          >
                            {apt.status === "completed" ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Completed</>
                            ) : (
                              <><XCircle className="w-3 h-3 mr-1" /> Cancelled</>
                            )}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* New Appointment Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-md" data-testid="new-appointment-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Schedule Appointment</DialogTitle>
            <DialogDescription>
              Book a follow-up session with a peer responder
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Responder Selection */}
            <div className="space-y-2">
              <Label className="label-uppercase">Select Responder</Label>
              <Select value={selectedResponder} onValueChange={setSelectedResponder}>
                <SelectTrigger data-testid="responder-select">
                  <SelectValue placeholder="Choose a responder" />
                </SelectTrigger>
                <SelectContent>
                  {responders.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No responders available</p>
                    </div>
                  ) : (
                    responders.map((responder) => (
                      <SelectItem key={responder.user_id} value={responder.user_id}>
                        {responder.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="label-uppercase">Select Date</Label>
              <div className="border border-border rounded-md p-3">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date()}
                  className="mx-auto"
                  data-testid="appointment-calendar"
                />
              </div>
            </div>

            {/* Time Selection */}
            <div className="space-y-2">
              <Label className="label-uppercase">Select Time</Label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger data-testid="time-select">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {new Date(`2000-01-01T${time}`).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="label-uppercase">Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any specific topics you'd like to discuss..."
                className="min-h-[80px]"
                data-testid="appointment-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAppointment}
              disabled={isSubmitting || !selectedDate || !selectedResponder}
              data-testid="schedule-appointment-btn"
            >
              {isSubmitting ? "Scheduling..." : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={!!cancelAppointmentId} onOpenChange={() => setCancelAppointmentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope' }}>Cancel Appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this appointment? You can schedule a new one at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelAppointment}
              className="bg-destructive hover:bg-destructive/90"
            >
              Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Global Footer Disclaimer */}
      <PeerSupportDisclaimer />
    </div>
  );
};

export default Appointments;
